import { StyleSheet } from 'aphrodite';
import { useMemo, type FC } from 'react';

import {
	Combobox,
	ComboboxCollection,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxGroupLabel,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
} from '../../../web/components-v2/Combobox';
import type { Category, Scenario, SkillArea } from './types';

export type ScenarioPickerProps = {
	scenarios: Scenario[];
	/** Skill areas — map a scenario (`skillAreaId`) up to its category (`categoryId`). */
	skillAreas: SkillArea[];
	/** Categories — the level scenarios are grouped under in the picker. */
	categories: Category[];
	/** Currently selected scenario id, or `null` for a random scenario. */
	selectedScenarioId: string | null;
	/** Fires with the chosen scenario id, or `null` when "Random scenario" is picked. */
	onSelect: (id: string | null) => void;
};

/**
 * Sentinel value for the "Random scenario" / none option. The combobox is a
 * controlled string-in/string-out component and can't carry `null` directly, so
 * we map `null` to this sentinel and translate back on change.
 */
const RANDOM_VALUE = '__random__';
const RANDOM_LABEL = 'Random scenario';

/** A combobox group: a skill-area label plus the scenario ids it contains. */
type PickerGroup = { value: string; items: string[] };

/**
 * Searchable, grouped single-select picker for practice scenarios, built on the
 * components-v2 Combobox. Scenarios are grouped by **category** — the top of the
 * legacy Category → Skill Area → Scenario hierarchy — because skill areas are
 * ~1:1 with scenarios and make useless one-item group headers. The join is
 * scenario → skill area (`skillAreaId`) → category (`categoryId`). A leading,
 * ungrouped "Random scenario" option maps to `null` (let the backend pick). No
 * scoring UI — scoring is out of scope for practice mode.
 */
export const ScenarioPicker: FC<ScenarioPickerProps> = ({
	scenarios,
	skillAreas,
	categories,
	selectedScenarioId,
	onSelect,
}) => {
	const nameById = useMemo(() => {
		const map = new Map<string, string>();
		scenarios.forEach(scenario => map.set(scenario.id, scenario.name));
		return map;
	}, [scenarios]);

	// Build [Random, ...category groups, Other?]. Resolve each scenario's category
	// via its skill area; scenarios with no resolvable category fall into a
	// trailing "Other" group so none are silently dropped.
	const groups = useMemo<PickerGroup[]>(() => {
		const categoryIdByAreaId = new Map<string, string>();
		skillAreas.forEach(area => categoryIdByAreaId.set(area.id, area.categoryId));

		const idsByCategory = new Map<string, string[]>();
		const orphanIds: string[] = [];
		scenarios.forEach(scenario => {
			const categoryId = categoryIdByAreaId.get(scenario.skillAreaId);
			if (!categoryId) {
				orphanIds.push(scenario.id);
				return;
			}
			const ids = idsByCategory.get(categoryId) ?? [];
			ids.push(scenario.id);
			idsByCategory.set(categoryId, ids);
		});

		const result: PickerGroup[] = [{ value: '', items: [RANDOM_VALUE] }];
		categories.forEach(category => {
			const ids = idsByCategory.get(category.id);
			if (ids && ids.length > 0) result.push({ value: category.name, items: ids });
		});

		if (orphanIds.length > 0) result.push({ value: 'Other', items: orphanIds });

		return result;
	}, [scenarios, skillAreas, categories]);

	const labelFor = (value: string): string => (value === RANDOM_VALUE ? RANDOM_LABEL : (nameById.get(value) ?? value));

	return (
		<Combobox
			items={groups}
			value={selectedScenarioId ?? RANDOM_VALUE}
			onValueChange={(value: unknown) => {
				const next = value as string;
				onSelect(next === RANDOM_VALUE ? null : next);
			}}
			itemToStringLabel={(value: unknown) => labelFor(value as string)}
		>
			<ComboboxInput placeholder='Search scenarios…' styles={[styles.input]} />
			<ComboboxContent>
				<ComboboxEmpty>No scenarios found.</ComboboxEmpty>
				<ComboboxList>
					{(group: PickerGroup) => (
						<ComboboxGroup key={group.value || '__random_group__'} items={group.items}>
							{group.value !== '' && <ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>}
							<ComboboxCollection>
								{(value: string) => (
									<ComboboxItem key={value} value={value}>
										<ComboboxItemIndicator />
										{labelFor(value)}
									</ComboboxItem>
								)}
							</ComboboxCollection>
						</ComboboxGroup>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

const styles = StyleSheet.create({
	input: {
		width: '100%',
	},
});
